import React, { useEffect, useMemo, useState } from "react";
import { getCiCompetitors, getCiRateChanges, markCiRateChangesRead } from "../../api/competitiveIntelligence";
import { RefetchBar } from "./AnalysisPanel";
import { InferredOccupancy } from "../../utils/ciFormat";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { SkeletonText } from "../shared/Skeleton";
import SelectMenu from "../shared/SelectMenu";
import { fmtRate, fmtPctSigned, fmtSignedRate, fmtDateShort, fmtDate, fmtNum } from "./shared";

// "2026-06-15" → "Jun 15". Plain string parse to dodge UTC shift.
// "2026-04-27T08:15:00Z" → "2d ago" (or "today" / "yesterday"). Sync is daily — minute
// precision on the detected timestamp doesn't tell the user anything actionable, and
// rendering a clock time on every card adds visual noise that makes the feed read like
// a debug log.
const fmtDetectedAt = (iso) => {
  if (!iso) return "—";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const days = Math.floor((Date.now() - timestamp) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
};

// A competitor's occupancy is only decision-relevant at the extremes — near 100% means
// they're effectively out of inventory (signal to raise), near 0% means weak demand
// (signal to hold/lower). Mid-range values just add noise to the row, so we hide them.
const OCC_HIGH_THRESHOLD = 75;
const OCC_LOW_THRESHOLD = 15;
const isOccNoteworthy = (value) =>
  value != null && Number.isFinite(Number(value)) && (Number(value) >= OCC_HIGH_THRESHOLD || Number(value) <= OCC_LOW_THRESHOLD);

const ChangeRow = ({ row, onMarkRead }) => {
  // Per spec: red up-arrow for increases (competitor raised), green down-arrow for decreases.
  const isIncrease = row.direction === "increase";
  const arrow = isIncrease ? "↑" : "↓";
  const arrowColor = isIncrease ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400";

  // Use the date_window_name when it carries weekend/holiday context ("Memorial Weekend",
  // "Weekend May 22") — otherwise fall back to a plain "Check-in May 22" so we never
  // render both halves of the same date side by side.
  const windowLabel = row.date_window_name || `Check-in ${fmtDateShort(row.check_in)}`;

  // Gap context: positive client_gap means client charges more than this competitor.
  // Phrase the gap as a quick read of where the change leaves you, instead of two
  // metadata lines ("Your rate: $X · gap +$Y").
  const gapContext = (() => {
    if (row.client_rate == null || row.client_gap_dollars == null) return null;
    const gapDollars = Number(row.client_gap_dollars);
    if (gapDollars > 0) return `you stay ${fmtSignedRate(gapDollars)} ahead`;
    if (gapDollars < 0) return `you sit ${fmtSignedRate(gapDollars)} behind`;
    return "matches your rate";
  })();

  // Occupancy: only render the competitor's number, and only when it's at an extreme
  // worth flagging. The client's own occupancy lives on the Rate Calendar — repeating it
  // on every change card adds noise without adding signal.
  const compOcc = row.competitor_inferred_occupancy_pct;
  const showCompOcc = isOccNoteworthy(compOcc);

  return (
    <li
      className={`relative px-5 py-3 flex items-start gap-3 ${
        row.is_read
          ? "opacity-60"
          : "bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-[#32B1FF]"
      }`}
    >
      <span className={`text-xl leading-none mt-0.5 ${arrowColor}`} aria-hidden="true">{arrow}</span>
      <div className="flex-1 min-w-0">
        {/* Line 1: who · tier · when */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-sm font-semibold truncate ${row.is_read ? "text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-gray-100"}`}>
            {row.competitor_name || "Unknown competitor"}
          </span>
          {row.tier && (
            <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              · {row.tier}
            </span>
          )}
          <span className="text-[11px] text-gray-500 dark:text-gray-400">· {windowLabel}</span>
          {!row.is_read && (
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#32B1FF]/15 text-[#32B1FF]">
              new
            </span>
          )}
        </div>

        {/* Line 2: what changed (price delta only) */}
        <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">
          {row.site_type_name && (
            <span className="text-gray-500 dark:text-gray-400">{row.site_type_name}: </span>
          )}
          {fmtRate(row.old_rate)} → <span className="font-semibold">{fmtRate(row.new_rate)}</span>
          <span className={`ml-2 ${arrowColor}`}>
            ({fmtSignedRate(row.rate_change)} · {fmtPctSigned(row.rate_change_pct)})
          </span>
        </div>

        {/* Line 3: contextual metadata — gap, occ, detection age. Kept terse and
            visually separated from the price change above so it doesn't read as
            a run-on. */}
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {gapContext && (
            <>
              <span className="text-gray-600 dark:text-gray-300">{gapContext}</span>
              <span className="mx-1.5">·</span>
            </>
          )}
          {showCompOcc && (
            <>
              Their occ <InferredOccupancy value={compOcc} className="text-gray-700 dark:text-gray-200" />
              <span className="mx-1.5">·</span>
            </>
          )}
          {fmtDetectedAt(row.detected_at)}
        </div>
      </div>
      {!row.is_read && (
        <button
          type="button"
          onClick={() => onMarkRead(row.id)}
          className="text-xs px-2 py-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          Mark read
        </button>
      )}
    </li>
  );
};

const Skeleton = () => (
  <div className="p-5">
    <SkeletonText lines={4} lineClass="h-12" gapClass="space-y-3" />
  </div>
);

const RateChangeFeedPanel = ({ clientId, categoryCode = null, onMarkRead = null }) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const [direction, setDirection] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [competitorId, setCompetitorId] = useState(null);
  // Full comp_set list, fetched once per client. Sourcing the Competitor dropdown
  // from this (instead of the current page's results) keeps the filter visible
  // even when the feed is empty or filtered down to zero rows.
  const [compSet, setCompSet] = useState([]);
  // Tracks change ids that the user marked read locally; the backend mark-read
  // route isn't wired yet, so this keeps the UI responsive without waiting on it.
  const [readIds, setReadIds] = useState(() => new Set());

  useEffect(() => {
    if (!clientId) { setCompSet([]); return; }
    let cancelled = false;
    getCiCompetitors(clientId)
      .then((resp) => {
        if (cancelled) return;
        setCompSet(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch(() => { if (!cancelled) setCompSet([]); });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    // Keep prior `data` visible during refilters so the chrome (filters,
    // counts, list) doesn't flicker — the RefetchBar + dimmed body convey
    // the in-flight state. First load still shows the skeleton because
    // `data` is null at that point.
    setState((prev) => ({ ...prev, loading: true, error: "" }));

    getCiRateChanges(clientId, {
      limit: 100,
      offset: 0,
      categoryCode: categoryCode || null,
      direction: direction || null,
      // Item 2 (work-page-fixes) — pass the real boolean (not `|| null`) so the
      // API helper can forward an explicit `false` to p_unread_only. The spec's
      // canonical feed fetch sends p_unread_only:false (show read + unread);
      // sending null made the backend drop read rows after a refresh.
      unreadOnly: unreadOnly,
      competitorAtlasParkId: competitorId == null ? null : competitorId,
    })
      .then((resp) => {
        if (cancelled) return;
        // Backend envelope is `{ error, data: { total, changes, monitoring }, message }`.
        // `changes` is always an array now (Brian E1); `monitoring` carries the
        // previous_sync timestamp + competitor/rate counts used in the empty state.
        const payload = resp?.data ?? resp ?? {};
        const changes = payload.changes ?? [];
        const monitoring = payload.monitoring ?? {};
        setState({
          loading: false,
          error: "",
          data: { total: payload.total ?? changes.length, changes, monitoring },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err?.message || "Failed to load rate changes", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, categoryCode, direction, unreadOnly, competitorId]);

  const rows = useMemo(() => {
    const list = state.data?.changes ?? [];
    return list.map((row) => (readIds.has(row.id) ? { ...row, is_read: true } : row));
  }, [state.data, readIds]);

  // Competitor dropdown sourced from the full comp_set, so the filter stays
  // available even when the feed is empty. Dedupes by atlas_park_id and sorts
  // by name for a predictable order.
  const competitorOptions = useMemo(() => {
    const seen = new Map();
    compSet.forEach((competitor) => {
      if (competitor.atlas_park_id != null && !seen.has(competitor.atlas_park_id)) {
        seen.set(competitor.atlas_park_id, competitor.competitor_name || `#${competitor.atlas_park_id}`);
      }
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));
  }, [compSet]);

  const total = state.data?.total ?? 0;
  const unreadCount = rows.filter((row) => !row.is_read).length;

  // Optimistic mark-read: update UI first, then fire the RPC. If it fails we keep the local
  // change so the row stays dimmed for this session, but log so we know the backend rejected.
  const handleMarkRead = (changeId) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(changeId);
      return next;
    });
    markCiRateChangesRead(clientId, [changeId])
      .then(() => onMarkRead?.())
      .catch((err) => {
        console.warn("markCiRateChangesRead failed:", err?.message || err);
      });
  };

  const handleMarkAllRead = () => {
    const ids = rows.filter((row) => !row.is_read).map((row) => row.id);
    if (!ids.length) return;
    setReadIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    // Pass null per spec — backend marks all unread for the client.
    markCiRateChangesRead(clientId, null)
      .then(() => onMarkRead?.())
      .catch((err) => {
        console.warn("markCiRateChangesRead (all) failed:", err?.message || err);
      });
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden min-h-[400px] md:min-h-[500px] lg:h-[720px] flex flex-col">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
            Rate Change Feed
            <InfoTip text="Chronological list of competitor rate moves (raises and drops) detected between syncs. A run of increases on one date is the market signaling rising demand — usually a cue to raise your own rate." />
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Chronological list of competitor rate moves detected since the last sync.
          </p>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-700 dark:text-gray-200">{total}</span> total
          {unreadCount > 0 && (
            <>
              <span className="mx-1.5">·</span>
              <span className="font-semibold text-[#32B1FF]">{unreadCount}</span> unread
            </>
          )}
        </div>
      </div>

      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">Direction:</span>
          <SelectMenu
            ariaLabel="Filter by direction"
            value={direction ?? null}
            onChange={setDirection}
            options={[
              { value: null, label: "All" },
              { value: "increase", label: "Increases" },
              { value: "decrease", label: "Decreases" },
            ]}
          />
        </div>

        {competitorOptions.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Competitor:</span>
            <SelectMenu
              ariaLabel="Filter by competitor"
              value={competitorId ?? null}
              onChange={setCompetitorId}
              options={[
                { value: null, label: "All" },
                ...competitorOptions.map((option) => ({ value: option.id, label: option.name })),
              ]}
            />
          </div>
        )}

        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => setUnreadOnly(event.target.checked)}
            className="rounded border-gray-300 dark:border-gray-700 text-[#32B1FF] focus:ring-[#32B1FF]"
          />
          Unread only
        </label>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAllRead}
            className="ml-auto text-xs px-2.5 py-1 rounded-md text-[#32B1FF] hover:bg-[#32B1FF]/10 transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* First load: full skeleton. Refilters: keep prior list visible,
          dim it, and show the slim refetch bar — same UX as paginated panels. */}
      {state.loading && state.data == null && !state.error && <Skeleton />}

      {state.error && (
        <ErrorState variant="inset" message={state.error} />
      )}

      {!state.error && state.data != null && (
        <div className="relative flex-1 flex flex-col min-h-0">
          {state.loading && <RefetchBar />}
          <div
            className={`flex-1 flex flex-col min-h-0 ${state.loading ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}`}
            aria-busy={state.loading}
          >
            {rows.length === 0 ? (
              (() => {
                const filtersActive = !!(direction || competitorId != null || unreadOnly);
                if (!filtersActive) {
                  const monitoring = state.data?.monitoring ?? {};
                  return (
                    <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
                      <div className="font-medium text-gray-700 dark:text-gray-200">
                        No price changes since {fmtDate(monitoring.previous_sync)}
                      </div>
                      <div className="text-xs">
                        Watching {fmtNum(monitoring.competitors_monitored)} competitors across {fmtNum(monitoring.rates_compared)} rates.
                      </div>
                    </div>
                  );
                }
                const activeFilters = [];
                if (direction) activeFilters.push(direction === "increase" ? "increases only" : "decreases only");
                if (competitorId != null) {
                  const name = competitorOptions.find((option) => option.id === competitorId)?.name;
                  if (name) activeFilters.push(name);
                }
                if (unreadOnly) activeFilters.push("unread only");
                return (
                  <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
                    <div className="font-medium text-gray-700 dark:text-gray-200">
                      No rate changes match your filters
                    </div>
                    <div className="text-xs">
                      Filtered by: <span className="font-medium">{activeFilters.join(" · ")}</span>.
                      {" "}
                      <button
                        type="button"
                        onClick={() => {
                          setDirection(null);
                          setCompetitorId(null);
                          setUnreadOnly(false);
                        }}
                        className="text-[#32B1FF] hover:underline font-medium"
                      >
                        Clear filters
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <ul className="ip-thin-scroll divide-y divide-gray-200 dark:divide-gray-800 flex-1 overflow-y-auto">
                {rows.map((row) => (
                  <ChangeRow key={row.id} row={row} onMarkRead={handleMarkRead} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RateChangeFeedPanel;
